import React, { type FC, Suspense, useCallback, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { IconButton, type IconButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import PlaylistAddedIcon from '@atlaskit/icon-lab/core/playlist-added';
import PlaylistRemoveIcon from '@atlaskit/icon-lab/core/playlist-remove';
import { Box } from '@atlaskit/primitives/compiled';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import { getConfluenceContentAri } from '../../effects/getConfluenceContentAri';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';
import type { FlagsContainer } from '../audio-player/AudioContainer';

import type { ModifyPlaylistButtonsQuery } from './__generated__/ModifyPlaylistButtonsQuery.graphql';

export interface baseProps {
	testId?: string;
	flags?: FlagsContainer;
	title?: string;
	circle?: boolean;
	subtle?: boolean;
}

interface playlistMutationArgs {
	cloudId: string;
	contentId: string;
	trackType?: `BRIEFING` | `NARRATION`;
	top?: boolean;
	contentType: string;
}

export interface ModifyPlaylistButtonProps extends baseProps {
	_defaultEngagement_do_not_use?: boolean;
	round?: boolean;
	Flags?: FlagsContainer;
	cloudId: string;
}

const messages = defineMessages({
	addToPlaylistText: {
		id: 'ai-audio.playlist.button.addToPlaylist-label',
		defaultMessage: 'Add to playlist',
		description: 'Label for the chevron up icon to collapse the playlist',
	},
	removeFromPlaylistText: {
		id: 'ai-audio.playlist.button.removeFromPlaylist-label',
		defaultMessage: 'Remove from playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
	inPlaylistText: {
		id: 'ai-audio.playlist.button.inPlaylist-label',
		defaultMessage: 'Added to playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
} as const);

const styles = cssMap({
	container: {
		//typecheck refuses to allow space.400 tokens into height or width
		height: '32px',
		width: '32px',
	},
});

export const AddToPlaylistButton: FC<baseProps & playlistMutationArgs> = ({
	testId, //for testing
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	circle, //optional to apply circle shape
	subtle, //optional to apply subtle treatment
	flags, //Flags Container,
}) => {
	const { formatMessage } = useIntl();
	const { addTrack, isAdding } = useContextMenuActions(flags, cloudId);

	const addToPlaylist = useCallback(async () => {
		await addTrack(
			getConfluenceContentAri(cloudId, contentId, contentType)?.toString() || '',
			[],
			(trackType || 'BRIEFING') as TrackType,
		);
	}, [cloudId, contentId, contentType, trackType, addTrack]);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		content: formatMessage(messages.addToPlaylistText),
		position: 'bottom',
		hideTooltipOnClick: true,
	};

	return (
		<IconButton
			icon={PlaylistAddIcon}
			testId={testId && `${testId}-add`}
			label={formatMessage(messages.addToPlaylistText)}
			tooltip={tooltipOptions}
			onClick={addToPlaylist}
			isTooltipDisabled={false}
			isDisabled={isAdding}
			shape={circle ? 'circle' : undefined}
			appearance={subtle ? 'subtle' : undefined}
		/>
	);
};

export const RemoveFromPlaylistButton: FC<baseProps & playlistMutationArgs> = ({
	testId, //for testing
	title, //for success flag text
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	circle, //optional to apply circle shape
	subtle, //optional to apply subtle treatment
	flags,
}) => {
	const { formatMessage } = useIntl();
	const { removeTrack, isRemoving } = useContextMenuActions(flags, cloudId);

	const removeFromPlaylist = useCallback(async () => {
		await removeTrack(
			getConfluenceContentAri(cloudId, contentId, contentType)?.toString() || '',
			[],
			(trackType || 'BRIEFING') as TrackType,
			title,
		);
	}, [cloudId, contentId, contentType, trackType, title, removeTrack]);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		content: formatMessage(messages.removeFromPlaylistText),
		position: 'bottom',
		hideTooltipOnClick: true,
	};

	return (
		<IconButton
			icon={PlaylistRemoveIcon}
			testId={testId && `${testId}-remove`}
			label={formatMessage(messages.removeFromPlaylistText)}
			tooltip={tooltipOptions}
			onClick={removeFromPlaylist}
			isTooltipDisabled={false}
			isDisabled={isRemoving}
			shape={circle ? 'circle' : undefined}
			appearance={subtle ? 'subtle' : undefined}
		/>
	);
};

//note: when using ModifyPlaylistButton abstraction the tooltip will not be seen because it changes to remove button on hover
export const InPlaylistButton: FC<baseProps> = ({ testId, circle, subtle }) => {
	const { formatMessage } = useIntl();

	const tooltipOptions: IconButtonProps['tooltip'] = {
		content: formatMessage(messages.inPlaylistText),
		position: 'bottom',
		hideTooltipOnClick: true,
	};
	return (
		<IconButton
			icon={PlaylistAddedIcon}
			testId={testId && `${testId}-added`}
			label={formatMessage(messages.inPlaylistText)}
			tooltip={tooltipOptions}
			isTooltipDisabled={false}
			shape={circle ? 'circle' : undefined}
			appearance={subtle ? 'subtle' : undefined}
		/>
	);
};

export const LazyModifyPlaylistButton: FC<ModifyPlaylistButtonProps & playlistMutationArgs> = ({
	testId, // test ID
	title, //text for remove flag
	round, //applies rounded treatment
	subtle, //applies subtle treatment
	_defaultEngagement_do_not_use = false,
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	flags,
}: ModifyPlaylistButtonProps & playlistMutationArgs) => {
	const playlist = useLazyLoadQuery<ModifyPlaylistButtonsQuery>(
		graphql`
			query ModifyPlaylistButtonsQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							mainContent {
								id
							}
						}
					}
				}
			}
		`,
		{ cloudId },
		{ fetchPolicy: 'store-or-network' },
	);

	const isInPlaylist = useMemo(() => {
		if (!playlist?.confluence_getPlaylist?.tracks?.nodes) {
			return false;
		}
		const contentIds =
			playlist.confluence_getPlaylist.tracks.nodes
				.map((node) => node?.mainContent?.id)
				.filter(Boolean) || [];
		return contentIds.includes(contentId);
	}, [playlist, contentId]);

	const [isEnagaged, setIsEngaged] = useState(_defaultEngagement_do_not_use);

	return (
		<Box
			// Set the state to true when the mouse enters or the element receives focus
			onMouseEnter={() => setIsEngaged(true)}
			onFocus={() => setIsEngaged(true)}
			// Set the state back to false when the mouse leaves or the element loses focus
			onMouseLeave={() => setIsEngaged(false)}
			onBlur={() => setIsEngaged(false)}
			xcss={styles.container}
		>
			{isInPlaylist ? (
				!isEnagaged ? (
					<InPlaylistButton testId={testId} circle={round} subtle={subtle} />
				) : (
					<RemoveFromPlaylistButton
						testId={testId}
						cloudId={cloudId}
						contentId={contentId}
						contentType={contentType}
						trackType={trackType}
						flags={flags}
						title={title}
						circle={round}
						subtle={subtle}
					/>
				)
			) : (
				<AddToPlaylistButton
					testId={testId}
					cloudId={cloudId}
					contentId={contentId}
					contentType={contentType}
					trackType={trackType}
					flags={flags}
					circle={round}
					subtle={subtle}
				/>
			)}
		</Box>
	);
};

export const ModifyPlaylistButton: FC<ModifyPlaylistButtonProps & playlistMutationArgs> = (
	props: ModifyPlaylistButtonProps & playlistMutationArgs,
) => {
	return (
		<Suspense fallback={null}>
			<LazyModifyPlaylistButton {...props} />
		</Suspense>
	);
};
